import React, { useEffect } from 'react';
import {
  Link,
  Route,
  Routes,
  Navigate,
  useParams,
  useNavigate,
  Outlet,
  useLocation
} from 'react-router-dom';
import './App.css';

import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from "react-i18next";

import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import BookAppointment from "./components/BookAppointment";

import Homepage from './pages/Homepage';
import Shop from "./pages/Shop";
import AboutUs from "./pages/AboutUs";
import Faq from './pages/Faq';
import ContactUs from "./pages/ContactUs";
import MechanicsPage from './pages/MechanicsPage';
import CarRentalPage from './pages/CarRentalPage';
import CarServicePage from './pages/CarServicePage';
import CarImportationPage from './pages/CarImportationPage';
import TermsAndConditions from './pages/TermsAndConditions';
import WorkWithUsPage from './pages/WorkWithUsPage';
import ArticlesPage from './pages/ArticlesPage';
import ArticlesDetailsPage from './pages/ArticlesDetailsPage';
import CategoryPage from "./pages/CategoryPage";
import VehicleDetailsPage from './pages/VehicleDetailsPage';

// 404 fallback
const PageNotFound = () => (
  <div style={{ textAlign: 'center', marginTop: '50px' }}>
    <h1>404 - Page Not Found</h1>
    <p>The page you are looking for does not exist.</p>
    <Link to="/" style={{ color: 'blue', textDecoration: 'underline' }}>
      Go back to Home
    </Link>
  </div>
);

// Language wrapper to sync route + i18n
// Language wrapper to sync route + i18n
const LanguageWrapper = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang === "ar") {
      navigate("/cm/fr", { replace: true });
      return;
    }

    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }

    document.body.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang, i18n, navigate]);

  return (
    <>
      <Header lang={lang} />
      <Outlet context={{ lang }} />
      <Footer lang={lang} />
    </>
  );
};


function App() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const supportedLangs = ["en", "fr", "es", "ar"];
  const browserLang = navigator.language.slice(0, 2);
  const defaultLang = supportedLangs.includes(browserLang) ? browserLang : "en";

  const changeLanguage = (code) => {
    navigate(`/cm/${code}`);
  };

  return (
    <div >
      <BookAppointment />
      <main>
        <HelmetProvider key={i18n.language}>
          <ScrollToTop />
          <Routes>
            <Route
              path="/"
              element={<Navigate to={`/cm/${defaultLang}`} replace />}
            />
            <Route path="/cm/:lang" element={<LanguageWrapper />}>
              <Route index element={<Homepage />} />
              <Route path="shop" element={<Shop />} />
              <Route path="about-us" element={<AboutUs />} />
              <Route path="faq" element={<Faq />} />
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="mechanics" element={<MechanicsPage />} />
              <Route path="car-rentals" element={<CarRentalPage />} />
              <Route path="car-servicing" element={<CarServicePage />} />
              <Route path="work-with-us" element={<WorkWithUsPage />} />
              <Route path="articles" element={<ArticlesPage />} />
              <Route path="articles/:slug" element={<ArticlesDetailsPage />} />
              <Route path="car-importation-consultation" element={<CarImportationPage />} />
              <Route path="terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="collections/:slug" element={<CategoryPage />} />
              <Route path="collections/:categorySlug/:vehicleSlug" element={<VehicleDetailsPage />} />
              <Route path="*" element={<PageNotFound />} />
            </Route>
          </Routes>
        </HelmetProvider>
      </main>
    </div>
  );
}

export default App;
